import { createAnimation } from "@ionic/core";
import { MotionProps, TargetAndTransition, Variants } from "framer-motion";

import { SlideDirectionEnum } from "@typing/Enums";

export const fadeInAndOutMotionProps = (delay: number): MotionProps => ({
  animate: { opacity: 1, transition: { delay, duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
  initial: { opacity: 0 }
});

export const fadeInMotionProps: MotionProps = {
  animate: { opacity: 1 },
  initial: { opacity: 0 },
  transition: { duration: 0.5 }
};

export const growMotionProps: MotionProps = {
  animate: { padding: "0 3px", width: "19px" },
  exit: { padding: 0, width: 0 },
  initial: { padding: 0, width: 0 },
  transition: {
    padding: { duration: 1.2, ease: "linear", type: "tween" },
    width: { delay: 0.2, duration: 0.8, ease: "linear", type: "tween" }
  }
};

export const inflateMotionProps: MotionProps = {
  animate: { height: "5000px", width: "5000px" },
  exit: { height: "1px", width: "1px" },
  initial: { height: "1px", width: "1px" },
  transition: {
    height: { duration: 1.2 },
    width: { duration: 1.2 }
  }
};

export const levitateMotionProps: MotionProps = {
  animate: { y: 0 },
  exit: { y: 1000 },
  initial: { y: 1000 },
  transition: {
    y: { duration: 1.2, ease: "linear", type: "tween" }
  }
};

type GenerateSliderMotionPropsArgs = {
  direction: SlideDirectionEnum;
};

type GenerateSliderMotionProps = (args: GenerateSliderMotionPropsArgs) => MotionProps;

export const generateSliderMotionProps: GenerateSliderMotionProps = ({ direction }) => ({
  animate: { x: 0 },
  exit: { position: "absolute", x: direction === SlideDirectionEnum.LEFT ? 2000 : -2000 },
  initial: { x: direction === SlideDirectionEnum.RIGHT ? 1000 : -1000 },
  transition: {
    x: { bounce: 0.3, duration: 1, type: "spring" }
  }
});

export const slideHorizontalAnimation = (swipeDirection: "left" | "right"): Variants => ({
  animate: {
    opacity: 1,
    transition: { duration: 0.3, type: "tween" },
    x: 0
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.3, type: "tween" },
    x: swipeDirection === "left" ? 300 : -300 // adjust values for off-screen exit
  },
  initial: {
    opacity: 0,
    x: swipeDirection === "left" ? -300 : 300 // adjust values for off-screen start
  }
});

export const fadeAnimation: Variants = {
  animate: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
  initial: { opacity: 0 }
};

export const modalSlideInAnimation = (baseEl: any) => {
  const root = baseEl.shadowRoot;

  const backdropAnimation = createAnimation()
    .addElement(root.querySelector("ion-backdrop"))
    .fromTo("opacity", "0.01", "var(--backdrop-opacity)");

  const wrapperAnimation = createAnimation()
    .addElement(root.querySelector(".modal-wrapper"))
    .keyframes([
      { offset: 0, opacity: "0", transform: "translateX(100%)" },
      { offset: 1, opacity: "1", transform: "translateX(0)" }
    ]);

  return createAnimation()
    .addElement(baseEl)
    .easing("ease-out")
    .duration(200)
    .addAnimation([backdropAnimation, wrapperAnimation]);
};

export const modalSlideOutAnimation = (baseEl: any) =>
  createAnimation()
    .addElement(baseEl.shadowRoot.querySelector(".modal-wrapper"))
    .fromTo("transform", "translateX(0)", "translateX(-100%)")
    .fromTo("opacity", "1", "0")
    .easing("ease-in")
    .duration(250);

export const slideVerticalAnimation = (swipeDirection: "up" | "down"): Variants => ({
  animate: {
    opacity: 1,
    y: swipeDirection === "up" ? 0 : "-100vh"
  },
  initial: {
    opacity: 0,
    y: swipeDirection === "up" ? "100vh" : 0
  }
});

export const moveUpAnimation = (pixels: number): TargetAndTransition => ({
  position: "relative",
  top: `-${pixels}px`,
  transition: {
    duration: 0.3
  }
});

export const shrinkMotionProps: MotionProps = {
  animate: {
    maxHeight: "200px",
    overflow: "hidden"
  },
  exit: {
    maxHeight: "0",
    overflow: "hidden",
    transition: { delay: 0.3, duration: 0.3 }
  },
  initial: {
    maxHeight: "200px"
  }
};
